import { resourceService } from "@libs/axios";
import { fetchListExamStudent, startExam, studentAnswerQuestion, studentFinishExam, fetchExamQuestionStatus } from "@store/actions/studentExamAction";
import { setStudentClassExamList, setLoading, setExamDetail, setExamDetailQuestions, setAnswerCountData, setExamError, resetStudentExamData } from "@store/reducers/studentExamReducer";
import { history } from "@utils/BrowserHistory";
import { toast } from "react-toastify";
import { put, call, takeLatest, cancelled } from "redux-saga/effects";
import { ResponseType } from '@store/index';
const API_URL = process.env.REACT_APP_API_URL;
const FALLBACK_EXAM_ERROR = 'Không tải được bài thi. Vui lòng quay lại trang thi thử và thử lại.';

const pickExamErrorText = (data: any): string | null => {
  if (data == null) return null;
  if (typeof data === 'string') {
    const trimmed = data.trim();
    if (!trimmed || trimmed.startsWith('<')) return null;
    return trimmed;
  }
  const detail = data.detail ?? data.message ?? data.error;
  if (typeof detail === 'string') {
    const trimmed = detail.trim();
    return trimmed || null;
  }
  if (Array.isArray(detail)) {
    const first = detail.find((item) => typeof item === 'string' && item.trim());
    return first ? first.trim() : null;
  }
  return null;
};

const translateErrorMessage = (detail: string | null) => {
  if (!detail) return FALLBACK_EXAM_ERROR;
  if (detail === 'This exam has not started yet.') {
    return 'Bài kiểm tra chưa bắt đầu';
  }
  if (detail === 'This exam has already ended.') {
    return 'Bài kiểm tra đã kết thúc';
  }
  if (detail === 'You have already completed the exam.') {
    return 'Bạn đã hoàn thành bài kiểm tra';
  }
  return detail;
};

const getStartExamErrorMessage = (error: any) =>
  translateErrorMessage(pickExamErrorText(error?.response?.data));


function* fetchListExamStudentSaga(action: ReturnType<typeof fetchListExamStudent>) {
  try {
    yield put(setLoading(true));
    const response: ResponseType = yield call(resourceService.get, API_URL + '/api/student_exams/')
    if (response.status === 200) {
      yield put(setStudentClassExamList(response?.data))
      yield put(setLoading(false));
    }
  }
  catch (error) {
    console.error(error);
    yield put(setLoading(false));
  }
}

function* startExamSaga(action: ReturnType<typeof startExam>): Generator<any, void, any> {
  try {
    yield put(setExamError(null))
    yield put(setLoading(true))
    const { moduleId, classId, submodule_class_practice_id, exam_extend_id } = action.payload
    let url = `${API_URL}/api/student_start_exams/${moduleId}/${classId}`
    const params = new URLSearchParams()
    if (submodule_class_practice_id != null) params.set('submodule_class_practice_id', String(submodule_class_practice_id))
    if (exam_extend_id != null) params.set('exam_extend_id', String(exam_extend_id))
    if (params.toString()) url += '?' + params.toString()
    const response: ResponseType = yield call(resourceService.get, url)
    if ((response.status === 201 || response.status === 200) && response?.data) {
      yield put(setExamDetail(response.data))
      let query = `id=${moduleId}&class=${classId}`
      if (submodule_class_practice_id != null) query += `&source=submodule_class_practice&source_id=${submodule_class_practice_id}`
      else if (exam_extend_id != null) query += `&source=exam_extend&source_id=${exam_extend_id}`
      if (!window.location.href?.includes('/student/examtest/test?')) {
        setTimeout(() => {
          history.push(`/student/examtest/test?${query}`)
        })
      }
    } else {
      yield put(setExamError(
        translateErrorMessage(pickExamErrorText(response?.data)),
      ))
    }
  }
  catch (error) {
    const isCancelled: boolean = yield cancelled()
    if (isCancelled) return
    console.error("error start exam", error)
    const examError = getStartExamErrorMessage(error)
    yield put(setExamError(examError))
    if (!window.location.href?.includes('/student/examtest/test?')) {
      toast.error(examError)
    }
  }
  finally {
    const isCancelled: boolean = yield cancelled()
    if (!isCancelled) {
      yield put(setLoading(false))
    }
  }
}

function* studentAnswerQuestionSaga(action: ReturnType<typeof studentAnswerQuestion>) {
  try {
    const { questionId } = action.payload;
    const response: ResponseType = yield call(resourceService.get, API_URL + `/api/student_answer_exam_question/${questionId}/`);
    if (response.status === 200) {
      yield put(setAnswerCountData({
        question_answer: response.data?.question_answer,
        question_count: response.data?.question_count
      }))
    }
  }
  catch (error) {
    console.error(error)
    toast.error('Có lỗi xảy ra, vui lòng thực hiện trả lời lại câu hỏi')
  }
}

function* studentFinishExamSaga(action: ReturnType<typeof studentFinishExam>) {
  const { moduleId } = action.payload
  yield put(setLoading(true))
  try {
    const response: ResponseType = yield call(resourceService.get, API_URL + `/api/student_end_exams/${moduleId}/`);
    if (response.status === 200) {
      yield put(setAnswerCountData({
        question_answer: response.data?.question_answer,
        question_count: response.data?.question_count,
        question_success: response.data?.question_success
      }))
    }
  }
  catch (error) {
    console.error(error)
    toast.error('Có lỗi xảy ra, vui lòng thực hiện trả lời lại câu hỏi')
  }
  finally {
    yield put(setLoading(false))
  }
}

function* fetchExamQuestionStatusSaga(action: ReturnType<typeof fetchExamQuestionStatus>) {
  try {
    const { examSessionId } = action.payload
    if (examSessionId == null || examSessionId === '') return
    const response: ResponseType = yield call(
      resourceService.get,
      API_URL + `/api/student_exam_question_status/${examSessionId}/`
    )
    if (response.status === 200 && response.data != null) {
      const questions = Array.isArray(response.data) ? response.data : response.data?.questions
      if (questions) {
        yield put(setExamDetailQuestions(questions))
      }
    }
  } catch (error) {
    console.error('fetch exam question status', error)
  }
}

function* watchStartExam(action: ReturnType<typeof startExam> | ReturnType<typeof resetStudentExamData>) {
  if (action.type === resetStudentExamData.type) return
  yield startExamSaga(action as ReturnType<typeof startExam>)
}

export default function* examSaga() {
  yield takeLatest(fetchListExamStudent, fetchListExamStudentSaga)
  yield takeLatest([startExam, resetStudentExamData], watchStartExam)
  yield takeLatest(studentAnswerQuestion, studentAnswerQuestionSaga)
  yield takeLatest(studentFinishExam, studentFinishExamSaga)
  yield takeLatest(fetchExamQuestionStatus, fetchExamQuestionStatusSaga)
} 